<template>
  <nav
    :class="[
      `tabs`,
      justified && `tabs--justified`,
      type === `compact` && `tabs--compact`,
      type === `overflow` && `tabs--scroll`,
    ]"
  >
    <div class="tabs__wrapper" data-test="tabs-wrapper">
      <a v-if="type === `compact`" :class="[`tabs__trigger`]" @click="onCompactTabClick()">
        <span class="tabs__trigger__label">{{ getActiveItem }}</span>
        <icon class-name="tabs__trigger__icon" name="chevron-down" />
      </a>
      <button
        v-if="type === `overflow`"
        class="tabs__previous"
        data-test="tabs-prev-button"
        aria-label="Scroll to the left"
        title="Scroll to the left"
        type="button"
        style="display: block"
        @click="scrollToDir('left')"
      >
        <icon class-name="tabs__previous__icon" name="chevron-left" />
      </button>
      <ul :class="[`tabs__list`, isCompactTabOpen && `tabs__list--active`]">
        <li
          v-for="item in items"
          :key="`${item.id}-${item.title}`"
          :data-id="`tabs-tab-${item.id}`"
          data-test="tabs-tab"
          class="tab tabs__list__item"
          @click="onTabsClick(item)"
        >
          <a
            :class="[
              `tabs__list__link`,
              item.active && `active hover focus`,
            ]"
          >
            <icon v-if="item.icon" class-name="tabs__list__icon" :name="item.icon" />
            {{ item.title }}
          </a>
        </li>
      </ul>
      <button
        v-if="type === `overflow`"
        class="tabs__next"
        data-test="tabs-next-button"
        aria-label="Scroll to the right"
        title="Scroll to the right"
        style="display: block;"
        @click="scrollToDir('right')"
      >
        <icon class-name="tabs__previous__icon" name="chevron-right" />
      </button>
    </div>
  </nav>
</template>

<script>
// Components
import Icon from "../Icon/Icon.vue";

export default {
  name: "Tabs",
  components: {
    Icon,
  },
  props: {
    /**
     * @description
     * Provides the Tab Items
     * Array of Objects
     * [{ "title":"Comments","id":"comments","active":true }]
     */
    items: {
      type: Array,
      required: true,
    },
    /**
     * @description
     * Provides the type of the Tab Component
     * Maersk Design System supports Three types Default, Compact and Overflow
     */
    type: {
      type: String,
      validator: (val) => ["default", "compact", "overflow"].includes(val),
      default: "default",
    },
    /**
     * @description
     * Checks if the Tab Bar should be Justified
     */
    justified: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isCompactTabOpen: false,
    };
  },
  computed: {
    /**
     * @description
     * Returns the Active Item Title that is used for Compact View Dropdown
     */
    getActiveItem() {
      return this.items.find((item) => item.active).title || "";
    },
  },
  methods: {
    /**
     * @description
     * Emits an Object that contains the Selected Tab, and updated Array of Objects with the appropriate active item
     * {
     *  selectedItemId: "case",
     *  updatedTabData: [{ "title":"Comments","id":"comments","active":true }, ...]
     * }
     * It also closes the dropdown in compact view
     */
    onTabsClick(selectedItem) {
      const updatedTabData = {
        selectedItemId: selectedItem.id,
        updatedTabData: this.items.map((item) => {
          return {
            ...item,
            active: item.id === selectedItem.id ? true : false,
          };
        }),
      };
      this.$emit("onClick", updatedTabData);
      // Scroll tabs
      if (this.type === "compact") {
        this.isCompactTabOpen = false;
      }
    },
    /**
     * @description
     * The method opens & closes the dropdown in compact view, to select tab options
     */
    onCompactTabClick() {
      this.isCompactTabOpen = !this.isCompactTabOpen;
    },
    /**
     * @description
     * The method returns the width of the tab bar
     */
    getListWidth() {
      return [...this.q("[data-test='tabs-tab']", true)].reduce(
        (width, el) => width + el.offsetWidth,
        0
      );
    },
    /**
     * @description
     * Scroll tabs: calculate the width of the tabs container
     * @return {number} The width of the tabs container
     */
    getContainerWidth() {
      const style = window.getComputedStyle(this.$el);
      return (
        this.$el.offsetWidth -
        parseInt(style.paddingLeft) -
        parseInt(style.paddingRight)
      );
    },
    /**
     * @description
     * Scroll tabs: show, hide, enable or disable the Prev/Next buttons
     */
    checkNavButtons() {
      // check whether nav buttons should be visible
      const hideNav = this.getListWidth() < this.getContainerWidth(); // no need for nav buttons
      this.$el.classList[hideNav ? "add" : "remove"]("tabs--scroll--no-nav");

      // check whether nav buttons should be enabled
      const listScroll = this.q("[data-test='tabs-wrapper']").scrollLeft; // current position
      this.q("[data-test='tabs-prev-button']").disabled = listScroll === 0; // beginning of the list: disable prev button
      this.q("[data-test='tabs-next-button']").disabled =
        listScroll + this.getContainerWidth() === this.getListWidth(); // end of the list
    },
    /**
     * @description
     * Scroll tabs: move the list either to the right or to the left
     * @param {string} dir - scroll direction: "left" or "right"
     */
    scrollToDir(dir) {
      const listScroll = this.q("[data-test='tabs-wrapper']").scrollLeft; // current position
      const rtl = dir === "left";
      this.scrollToPos(listScroll + (rtl ? -1 : 1) * this.getScrollAmount(rtl));
    },
    /**
     * @description
     * Scroll tabs: calculate the number of pixels the list should scroll when nav buttons are clicked
     * @param {boolean} rtl - indicate if the scroll is from right to left, i.e. Previous button was clicked (defaults to false)
     * @return {number} The amount to scroll
     */
    getScrollAmount(rtl = false) {
      // the number of pixels the list is currently scrolled by
      const listScroll = this.q("[data-test='tabs-wrapper']").scrollLeft;
      // the number of visible tabs in the viewport
      const $tabsVisible = [...this.q("[data-test='tabs-tab']", true)].filter(
        (tab) =>
          tab.offsetLeft >= listScroll &&
          tab.offsetLeft + tab.offsetWidth <
            listScroll + this.getContainerWidth()
      );
      // number of tabs to hide when scrolling: 50% of visible tabs (minimum 1 for narrow screens)
      // when scrolling from left to right hide tabs from the left side of the list
      const tabsToHide = Math.max(1, Math.floor($tabsVisible.length * 0.5));
      // scrolling from right to left (Previous button)
      // hide tabs from the right side of the list
      if (rtl) {
        $tabsVisible.reverse();
      }
      // the amount to scroll: sum the tabs' width that will get hidden
      return $tabsVisible
        .slice(0, tabsToHide)
        .reduce((w, tab) => w + tab.offsetWidth, 0);
    },
    /**
     * @description
     * Scroll tabs: move the list to the specified position
     * @param {number} pos - the position to scroll the list to
     * @return {promise} the promise resolves when the scroll finishes
     */
    scrollToPos(pos) {
      const tabs = this.q("[data-test='tabs-wrapper']");
      // check the viewport's boundaries
      pos = Math.min(
        Math.max(pos, 0),
        this.getListWidth() - this.getContainerWidth()
      );
      const scrollPromise = new Promise((resolve) => {
        const listener = (e) => {
          if (parseInt(e.currentTarget.scrollLeft) === parseInt(pos)) {
            e.currentTarget.removeEventListener("scroll", listener);
            resolve();
          }
        };
        tabs.addEventListener("scroll", listener);
        window.requestAnimationFrame(() => {
          tabs.scrollTo({ left: pos, behavior: "smooth" });
        });
      });
      scrollPromise.then(this.checkNavButtons);
      return scrollPromise;
    },
    /**
     * @description
     * Scoped querySelector shorthand
     */
    q(selector, shouldSelectAll = false) {
      return this.$el[shouldSelectAll ? "querySelectorAll" : "querySelector"](
        selector
      );
    },
  },
};
</script>

<style lang="scss">
.tabs {
  &.tabs--scroll {
    padding-left: 40px;
    padding-right: 40px;

    & .tabs__list {
      width: 99999px;
    }
  }

  & .tabs__list__item {
    cursor: pointer;
  }
}
</style>